import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { jwtDecode } from 'jwt-decode';
import { of } from "rxjs";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";
import { AccessType } from "src/app/api/models";
import { CityService, InsurerService, MaintenanceService, ProvinceService, ReferentialService, SiteService } from "src/app/api/services";
import { UserLogin } from "src/app/authenticator/models";
import { AuthenticationService } from "src/app/authenticator/services";
import { loadSysparams } from "src/app/kiosk/state/kiosk.actions";
import { AppState } from "src/app/state/app.state";
import { environment } from "src/environments/environment";
import { setSelectedSite, setSelectedCity, setSelectedProvince, setBirthdate, setCardNumber, setCardIdShowPage, setLoginError } from "../../transaction-info/state/transaction-info.actions";
import { getFullAccess, getFullAccessFailure, getFullAccessSuccess, loginLight, loginLightFailure, loginLightSuccess, loginWithCertificate, loginWithCertificateFailure, loginWithCertificateSuccess, setKioskSite, setKioskSiteFailure, setKioskSiteSuccess } from "./authentication.actions";

@Injectable()
export class AuthenticationEffects {
	constructor(private actions$: Actions, private authenticationService: AuthenticationService, private insurerService: InsurerService,
		private referentialService: ReferentialService, private siteService: SiteService, private cityService: CityService, private provinceService: ProvinceService, private store: Store<AppState>,
		private maintenanceService: MaintenanceService) { }

	public loginLight$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(loginLight),
			switchMap(() => {
				return this.authenticationService.authenticationLoginLight({accessType: environment.kioskMode ? AccessType.Kiosk : AccessType.Home}).pipe(
					map((token) => {
						return loginLightSuccess({ data: token });
					}),
					catchError((err) => {
						return of(loginLightFailure({ data: err }))
					})
				)
			})
		);
	});

	public loginLightSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(loginLightSuccess, loginWithCertificateSuccess),
			switchMap(() => {
				return [loadSysparams()];
			}
			)
		);
	});

	public getFullAccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(getFullAccess),
			switchMap(({ insurerId, cardId, dob }) => {
				this.store.dispatch(setLoginError({ data: "" }));
				return this.authenticationService.authenticationGetFullAccess({ insurerId: insurerId, cardId: cardId, dob: dob,
					accessType: environment.kioskMode ? AccessType.Kiosk : AccessType.Home }).pipe(
					map((token) => {
						this.store.dispatch(setLoginError({ data: undefined }));
						return getFullAccessSuccess({ token: token});
					}),
					catchError((err) => {
						this.store.dispatch(setBirthdate({ data: undefined }));
						this.store.dispatch(setLoginError({ data: err.error.message }));
						return of(getFullAccessFailure({ data: err }));
					})
				)
			})
		);
	});

	public loginWithCertificate$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(loginWithCertificate),
			switchMap(() => {
				if (environment.authenticatorUrl.indexOf('localhost') == -1) {
					return this.authenticationService.authenticationLoginWithCertificate({accessType: environment.kioskMode ? AccessType.Kiosk : AccessType.Home}).pipe(
						map((token) => loginWithCertificateSuccess({ data: token })),
						catchError((err) => of(loginWithCertificateFailure({ data: err })))
					)
				}
				else {
					return this.authenticationService.authenticationLogin({ body: { login: environment.login, password: environment.password,
						 accessType: environment.kioskMode ? AccessType.Kiosk : AccessType.Home } as UserLogin }).pipe(
						map((token) => loginWithCertificateSuccess({ data: token })),
						catchError((err) => of(loginWithCertificateFailure({ data: err })))
					)
				}
			})
		)
	});

	public setKioskSite$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(setKioskSite),
			switchMap(({ token }) => {
				return this.siteService.siteGetForKiosk({ SiteId: jwtDecode(token.access_token)["defaultSiteId"] }).pipe(
					map((site) => {
						this.store.dispatch(setSelectedSite({ data: site[0] }));
						return site[0];
					}),
					switchMap((site) => {
						return this.cityService.cityGet({ CityId: site.cityId }).pipe(
							map((city) => {
								this.store.dispatch(setSelectedCity({ data: city[0] }));
								return city[0];
							})
						);
					}),
					switchMap((city) => {
						return this.provinceService.provinceGet({ ProvinceId: city.provinceId }).pipe(
							map((province) => {
								this.store.dispatch(setSelectedProvince({ data: province[0] }));
							})
						);
					}),
					switchMap(() => of(setKioskSiteSuccess())),
					catchError((err) => of(setKioskSiteFailure()))
				)
			}))
	});
}